import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import api from '../../api/api';
import '../../styles/Dashboard.css';

// Custom hook for child progress - simplified version of mobile version
const useChildProgress = (childId) => {
  const [stats, setStats] = useState({
    assignmentCompletion: 0,
    assessmentAverage: { value: 0, text: 'N/A' },
    attendanceRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!childId) {
      setLoading(false);
      return;
    }

    const fetchProgress = async () => {
      try {
        // Use existing parent-specific API endpoints
        const [assignmentsResponse, attendanceResponse] = await Promise.allSettled([
          api.get(`/parents/children/${childId}/assignments`),
          api.get(`/parents/children/${childId}/attendance`)
        ]);

        // Process assignments data
        if (assignmentsResponse.status === 'fulfilled') {
          const assignmentsData = assignmentsResponse.value.data.data || [];
          
          // Calculate metrics from assignments
          const totalAssignments = assignmentsData.length;
          const completedAssignments = assignmentsData.filter(assignment => 
            assignment.status === 'completed' &&
            (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat')
          ).length;
          
          const completionRate = totalAssignments > 0 
            ? Math.round((completedAssignments / totalAssignments) * 100) 
            : 0;
          
          // Calculate assessment average
          const assessedAssignments = assignmentsData.filter(assignment => 
            assignment.assessment && assignment.assessment.rating
          );
          
          let assessmentAvgValue = 0;
          let assessmentText = 'N/A';
          
          if (assessedAssignments.length > 0) {
            // Map ratings to numerical values
            const ratingValues = {
              'Excellent': 5,
              'Very Good': 4,
              'Good': 3,
              'Pass': 2,
              'Need to Repeat': 1
            };
            
            const assessmentSum = assessedAssignments.reduce((sum, assignment) => {
              return sum + (ratingValues[assignment.assessment.rating] || 0);
            }, 0);
            
            assessmentAvgValue = assessmentSum / assessedAssignments.length;
            
            // Determine text rating based on average
            if (assessmentAvgValue >= 4.5) assessmentText = 'Excellent';
            else if (assessmentAvgValue >= 3.5) assessmentText = 'Very Good';
            else if (assessmentAvgValue >= 2.5) assessmentText = 'Good';
            else if (assessmentAvgValue >= 1.5) assessmentText = 'Pass';
            else assessmentText = 'Need to Repeat';
          }

          // Update partial stats with assignment data
          setStats(prev => ({
            ...prev,
            assignmentCompletion: completionRate,
            assessmentAverage: { 
              value: assessmentAvgValue > 0 ? parseFloat(assessmentAvgValue.toFixed(1)) : 0, 
              text: assessmentText 
            }
          }));
        }

        // Process attendance data
        if (attendanceResponse.status === 'fulfilled') {
          const attendanceStats = attendanceResponse.value.data.stats;
          
          // Extract attendance rate from the stats
          if (attendanceStats && attendanceStats.attendanceRate) {
            const attendanceRate = parseInt(attendanceStats.attendanceRate.replace('%', '')) || 0;
            
            // Update stats with attendance rate
            setStats(prev => ({
              ...prev,
              attendanceRate: attendanceRate
            }));
          }
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching child progress data:', err);
        setError('Failed to load progress data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProgress();
  }, [childId]);

  return { stats, loading, error };
};

// Child Card Component
const ChildCard = ({ child, onCardClick }) => {
  const firstLetter = child.name ? child.name.charAt(0).toUpperCase() : '?';
  const { stats } = useChildProgress(child._id || child.id);
  const [learningPlans, setLearningPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(() => {
    // Load expanded state from localStorage
    const saved = localStorage.getItem(`child-${child._id || child.id}-expanded`);
    return saved === 'true';
  });

  // Toggle expanded state and save to localStorage
  const toggleExpanded = (e) => {
    e.stopPropagation();
    const newState = !isExpanded;
    setIsExpanded(newState);
    localStorage.setItem(`child-${child._id || child.id}-expanded`, newState);
  };

  // Fetch learning plans for this child
  useEffect(() => {
    const fetchLearningPlans = async () => {
      if (!child._id && !child.id) {
        setPlansLoading(false);
        return;
      }

      try {
        const childId = child._id || child.id;
        const response = await api.get(`/learning-plans/student/${childId}`);
        const plans = response.data.data || [];

        // Filter for active plans only (max 3: new-memorization, revision, recitation)
        const activePlans = plans.filter(p => p.status === 'active');

        // Enrich each plan with average rating, last/next assignments
        const enrichedPlans = await Promise.all(
          activePlans.map(async (plan) => {
            try {
              const assignmentsResponse = await api.get(`/learning-plans/${plan._id}/assignments`);
              const assignments = assignmentsResponse.data.data || [];

              // Calculate average rating (excluding "Need to Repeat")
              const getRatingValue = (rating) => {
                const ratingMap = {
                  'Excellent': 5,
                  'Very Good': 4,
                  'Good': 3,
                  'Pass': 2,
                  'Weak': 1
                };
                return ratingMap[rating] || 0;
              };

              const ratedAssignments = assignments.filter(a =>
                a.assessment &&
                a.assessment.rating &&
                a.assessment.rating !== 'Need to Repeat'
              );

              if (ratedAssignments.length > 0) {
                const totalRating = ratedAssignments.reduce((sum, assignment) => {
                  return sum + getRatingValue(assignment.assessment.rating);
                }, 0);

                plan.averageRating = (totalRating / ratedAssignments.length).toFixed(1);
                plan.ratedAssignmentsCount = ratedAssignments.length;
              }

              // Find last completed assignment
              const completedAssignments = assignments
                .filter(a => a.status === 'completed')
                .sort((a, b) => {
                  const dateA = a.assessment?.assessedAt || a.assessment?.assessedDate || a.updatedAt;
                  const dateB = b.assessment?.assessedAt || b.assessment?.assessedDate || b.updatedAt;
                  return new Date(dateB) - new Date(dateA);
                });

              plan.lastAssignment = completedAssignments[0] || null;

              // Find next pending assignment (active, pending, or in-progress, sorted by due date)
              const pendingAssignments = assignments
                .filter(a =>
                  a.status === 'active' ||
                  a.status === 'pending' ||
                  a.status === 'in-progress'
                )
                .sort((a, b) => {
                  const dateA = a.dueDate ? new Date(a.dueDate) : new Date(0);
                  const dateB = b.dueDate ? new Date(b.dueDate) : new Date(0);
                  return dateA - dateB;
                });

              plan.nextAssignment = pendingAssignments[0] || null;

              // Calculate status based on overdue assignments (matching teacher/admin view)
              const overdue = plan.progress?.due || 0;
              if (overdue === 0) {
                plan.statusIndicator = 'on-track';
              } else if (overdue >= 3) {
                plan.statusIndicator = 'behind';
              } else {
                plan.statusIndicator = 'behind'; // Slightly behind
              }

              return plan;
            } catch (error) {
              console.error('Error fetching plan assignments for rating:', error);
              return plan;
            }
          })
        );

        setLearningPlans(enrichedPlans);
      } catch (error) {
        console.error('Error fetching learning plans:', error);
        setLearningPlans([]);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchLearningPlans();
  }, [child._id, child.id]);

  // Helper function for progress bar colors
  const getPlanProgressColor = (percent) => {
    if (percent >= 75) return '#4caf50'; // Green
    if (percent >= 50) return '#2196f3'; // Blue
    if (percent >= 25) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  // Helper function to format assignment type
  const formatAssignmentType = (type) => {
    if (!type) return 'New Memorization';
    return type.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Helper function to format assignment content
  const formatAssignmentContent = (assignment) => {
    if (!assignment) return 'N/A';

    // First, try to use the title if available (most reliable)
    if (assignment.title) {
      return assignment.title;
    }

    // Handle new structure with 'range' property
    if (assignment.range) {
      const range = assignment.range;
      if (range.start && range.end) {
        const surahName = range.start.surahName || range.start.surah?.name || 'Surah';
        const verses = `${range.start.verse || 1}-${range.end.verse || 1}`;
        return `${surahName} (${verses})`;
      }
    }

    // Handle old structure with 'content' property
    const content = assignment.content;
    if (content) {
      // Handle surah-based assignments
      if (content.surah) {
        const surahName = content.surah.name || content.surah.englishName || `Surah ${content.surah.number || ''}`;
        const verseRange = content.verseRange && content.verseRange.from && content.verseRange.to
          ? ` (${content.verseRange.from}-${content.verseRange.to})`
          : '';
        return `${surahName}${verseRange}`;
      }

      // Handle single page
      if (content.page) {
        return `Page ${content.page}`;
      }

      // Handle multiple pages array
      if (content.pages && content.pages.length > 0) {
        return content.pages.length === 1
          ? `Page ${content.pages[0]}`
          : `Pages ${content.pages[0]}-${content.pages[content.pages.length - 1]}`;
      }

      // Handle juz-based assignments
      if (content.juz) {
        return `Juz ${content.juz.number || content.juz}`;
      }

      // Handle line-based assignments
      if (content.lines) {
        return `${content.lines} lines`;
      }
    }

    // Handle metadata.pages as fallback
    if (assignment.metadata && assignment.metadata.pages && assignment.metadata.pages.length > 0) {
      const pages = assignment.metadata.pages;
      return pages.length === 1
        ? `Page ${pages[0]}`
        : `Pages ${pages[0]}-${pages[pages.length - 1]}`;
    }

    return assignment.description || 'Assignment';
  };

  // Helper function to format relative date
  const formatRelativeDate = (date) => {
    if (!date) return '';

    try {
      const now = new Date();
      const targetDate = new Date(date);

      // Check if date is valid
      if (isNaN(targetDate.getTime())) return '';

      const diffTime = targetDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'today';
      if (diffDays === 1) return 'tomorrow';
      if (diffDays === -1) return 'yesterday';
      if (diffDays > 1) return `in ${diffDays} days`;
      if (diffDays < -1) return `${Math.abs(diffDays)} days ago`;
      return '';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Helper function to get status display
  const getStatusDisplay = (status) => {
    const statusMap = {
      'on-track': { text: 'On Track', color: '#4caf50', icon: '●' },
      'behind': { text: 'Behind', color: '#ff9800', icon: '●' },
      'ahead': { text: 'Ahead', color: '#2196f3', icon: '●' }
    };
    return statusMap[status] || statusMap['on-track'];
  };

  return (
    <div className="child-card" onClick={() => onCardClick(child)}>
      <div className="child-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="avatar">
            <span>{firstLetter}</span>
          </div>
          <div className="child-details">
            <h3>{child.name || 'Unknown'}</h3>
            {child.hasMultipleParents && (
              <div className="parent-indicator" style={{
                fontSize: '12px',
                color: '#666',
                backgroundColor: '#e8f4fd',
                padding: '2px 8px',
                borderRadius: '12px',
                marginTop: '4px',
                display: 'inline-block'
              }}>
                👨‍👩‍👧‍👦 {child.parentCount} parents
              </div>
            )}
          </div>
        </div>
        <button
          onClick={toggleExpanded}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            padding: '8px',
            color: '#666'
          }}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? '▲' : '▼'}
        </button>
      </div>

      {isExpanded && (
        <div className="stats-container">
          <div className="stat-box assignment-stat">
            <div className="stat-value">{stats.assignmentCompletion}%</div>
            <div className="stat-label">Assignments</div>
          </div>

          <div className="stat-box rating-stat">
            <div className="stat-value">{stats.assessmentAverage.value}/5</div>
            <div className="stat-label">Avg. Rating</div>
          </div>

          <div className="stat-box attendance-stat">
            <div className="stat-value">{stats.attendanceRate}%</div>
            <div className="stat-label">Attendance</div>
          </div>
        </div>
      )}

      {/* Learning Plans Section */}
      {!plansLoading && (
        <div className="learning-plans-summary"
          onClick={(e) => e.stopPropagation()}
          style={{
          marginTop: '15px',
          paddingTop: '15px',
          borderTop: '1px solid #e0e0e0'
        }}>
          {learningPlans.length > 0 ? (
            <>
              <div style={{
                fontSize: '16px',
                color: '#666',
                marginBottom: '12px',
                fontWeight: '500'
              }}>
                Learning Plans ({learningPlans.length})
              </div>
              {learningPlans.map((plan, index) => {
                const statusDisplay = getStatusDisplay(plan.statusIndicator || 'on-track');
                const isLastPlan = index === learningPlans.length - 1;
                return (
                  <div key={plan._id} style={{
                    marginBottom: '24px',
                    paddingBottom: isLastPlan ? '0' : '20px',
                    borderBottom: isLastPlan ? 'none' : '1px solid #e8e8e8'
                  }}>
                    {/* Plan header with type, rating, status, and progress */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '5px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '14px',
                          color: '#333',
                          fontWeight: '500'
                        }}>
                          {formatAssignmentType(plan.assignmentType)}
                        </span>
                        {plan.averageRating && (
                          <span style={{
                            fontSize: '11px',
                            color: '#1976d2',
                            backgroundColor: '#e3f2fd',
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontWeight: '600'
                          }}>
                            {plan.averageRating}/5 Avg
                          </span>
                        )}
                        <span style={{
                          fontSize: '11px',
                          color: statusDisplay.color,
                          backgroundColor: `${statusDisplay.color}15`,
                          padding: '2px 8px',
                          borderRadius: '8px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <span style={{ fontSize: '8px' }}>{statusDisplay.icon}</span>
                          {statusDisplay.text}
                        </span>
                      </div>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#4a90e2'
                      }}>
                        {plan.progress?.percent || 0}%
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        width: `${plan.progress?.percent || 0}%`,
                        height: '100%',
                        backgroundColor: getPlanProgressColor(plan.progress?.percent || 0),
                        transition: 'width 0.3s ease'
                      }} />
                    </div>

                    {/* Last and Next Assignment */}
                    <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                      {plan.lastAssignment && (
                        <div style={{
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          marginBottom: '4px',
                          flexWrap: 'wrap'
                        }}>
                          <span style={{ color: '#4caf50', fontWeight: 'bold' }}>✓</span>
                          <span>Last: {formatAssignmentContent(plan.lastAssignment)}</span>
                          {(plan.lastAssignment.assessment?.assessedAt || plan.lastAssignment.assessment?.assessedDate || plan.lastAssignment.updatedAt) && (
                            <span style={{ color: '#999' }}>
                              ({formatRelativeDate(plan.lastAssignment.assessment?.assessedAt || plan.lastAssignment.assessment?.assessedDate || plan.lastAssignment.updatedAt)})
                            </span>
                          )}
                          {plan.lastAssignment.assessment?.rating && (
                            <span style={{
                              fontSize: '10px',
                              backgroundColor: '#e8f5e9',
                              color: '#4caf50',
                              padding: '1px 5px',
                              borderRadius: '6px',
                              fontWeight: '600'
                            }}>
                              {plan.lastAssignment.assessment.rating}
                            </span>
                          )}
                        </div>
                      )}
                      {plan.nextAssignment && (
                        <div style={{
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          flexWrap: 'wrap'
                        }}>
                          <span style={{ color: '#2196f3', fontWeight: 'bold' }}>→</span>
                          <span>Next: {formatAssignmentContent(plan.nextAssignment)}</span>
                          {plan.nextAssignment.dueDate && (
                            <span style={{ color: '#999' }}>
                              (Due {formatRelativeDate(plan.nextAssignment.dueDate)})
                            </span>
                          )}
                        </div>
                      )}
                      {!plan.lastAssignment && !plan.nextAssignment && (
                        <div style={{ color: '#999', fontStyle: 'italic' }}>
                          No assignment activity yet
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div style={{
              fontSize: '12px',
              color: '#999',
              textAlign: 'center',
              padding: '8px 0'
            }}>
              No active learning plans
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ParentDashboard = ({ user: propUser, data }) => {
  // Get user from context if not provided as prop
  const authContext = useContext(AuthContext);
  const contextUser = authContext?.user;
  const { logout } = authContext;
  const navigate = useNavigate();
  
  // Use either the prop user or context user
  const user = propUser || contextUser || { name: 'Parent' };
  
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch children data
  useEffect(() => {
    const fetchChildren = async () => {
      if (!user || !user.id) {
        console.log('ParentDashboard: No user ID available, cannot fetch children');
        setLoading(false);
        return;
      }

      console.log('ParentDashboard: Fetching children for parent ID:', user.id);
      setLoading(true);
      try {
        const response = await api.get(`/parents/children/${user.id}`);
        console.log('ParentDashboard: Children API response:', response.data);

        if (response.data.success && response.data.data.length > 0) {
          console.log(`ParentDashboard: Found ${response.data.data.length} children`);

          // Enhance children data with parent count information
          const childrenWithParentInfo = await Promise.all(
            response.data.data.map(async (child) => {
              try {
                const parentsResponse = await api.get(`/students/${child._id}/parents`);
                const parentCount = parentsResponse.data.data?.length || 1;
                return {
                  ...child,
                  parentCount,
                  hasMultipleParents: parentCount > 1
                };
              } catch (error) {
                console.error('Error fetching parent count for child:', child.name, error);
                return {
                  ...child,
                  parentCount: 1,
                  hasMultipleParents: false
                };
              }
            })
          );

          setChildren(childrenWithParentInfo);
        } else {
          console.log('ParentDashboard: No children data returned from API');
          setChildren([]);
        }
      } catch (error) {
        console.error('ParentDashboard: Error fetching children:', error);
        setError('Failed to load children. Please try again.');
        setChildren([]);
      } finally {
        console.log('ParentDashboard: Finished loading children data');
        setLoading(false);
      }
    };

    fetchChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  
  // Handle click on child card
  const handleChildCardClick = (child) => {
    console.log('Clicked on child:', child.name);
    
    // Navigate to the Child Profile Page
    navigate(`/parent/children/${child._id || child.id}`, {
      state: {
        childId: child._id || child.id,
        childName: child.name
      }
    });
  };
  
  // Handle logout with confirmation
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  // Calculate summary stats for welcome message
  const totalChildren = children.length;
  const sharedChildren = children.filter(child => child.hasMultipleParents).length;

  return (
    <div className="dashboard-container">
      <div className="dashboard-welcome">
        <h1>Welcome, {user.name || 'Parent'}</h1>
        <div className="welcome-summary">
          <p>You have {totalChildren} {totalChildren === 1 ? 'child' : 'children'} enrolled</p>
          {sharedChildren > 0 && (
            <p style={{ 
              fontSize: '14px', 
              color: '#666', 
              backgroundColor: '#f8f9fa',
              padding: '8px 12px',
              borderRadius: '6px',
              marginTop: '8px',
              border: '1px solid #e9ecef'
            }}>
              ℹ️ {sharedChildren} {sharedChildren === 1 ? 'child has' : 'children have'} multiple parents/guardians
            </p>
          )}
        </div>
      </div>
      
      <div className="dashboard-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading children data...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Try Again</button>
          </div>
        ) : children.length > 0 ? (
          <div className="children-list">
            {children.map((child) => (
              <ChildCard 
                key={child._id || child.id} 
                child={child} 
                onCardClick={handleChildCardClick} 
              />
            ))}
          </div>
        ) : (
          <div className="content-card">
            <h2>Your Children</h2>
            <div className="empty-state">
              <p>No children linked to your account.</p>
              <p>Please contact your School or Centre Administrator.</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Logout button */}
      <div className="logout-container">
        <button className="logout-button" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </div>
  );
};

export default ParentDashboard;